import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { qk } from '../keys';
import { useEventStream, type StreamStatus } from '../sse';
import type { BoardSnapshot } from '../types';

const POLL_INTERVAL_MS = 20_000;

/**
 * Бордът се зарежда веднъж и после се обновява от потока. Ако потокът падне,
 * `polling` включва допитване на 20 секунди — при 11 буса това е нищо за
 * сървъра и достатъчно бързо за човека.
 */
export function useBoard(): {
  snapshot: BoardSnapshot | undefined;
  isLoading: boolean;
  isError: boolean;
  streamStatus: StreamStatus;
  lastMessageAt: number | null;
  refresh: () => void;
} {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: qk.board.snapshot(),
    queryFn: () => api.get<BoardSnapshot>('/dispatch/board'),
    staleTime: 10_000,
  });

  /**
   * Сървърът праща едно събитие — `change` — с вид на промяната (назначение,
   * тръгване, приключване, позиция). Бордът се презарежда, но не повече от
   * веднъж на 2 секунди: 11 буса с позиция на всеки 30 секунди иначе биха
   * дърпали борда постоянно.
   */
  const refreshTimer = useRef<number | null>(null);
  const touched = useRef({ orders: false });
  const handleMessage = useCallback(
    (message: { event: string; data: string }) => {
      if (message.event !== 'change') return;
      let change: { type?: string };
      try {
        change = JSON.parse(message.data) as { type?: string };
      } catch {
        return;
      }
      if (change.type !== 'position') touched.current.orders = true;
      if (refreshTimer.current !== null) return;
      refreshTimer.current = window.setTimeout(() => {
        refreshTimer.current = null;
        void queryClient.invalidateQueries({ queryKey: qk.board.all });
        if (touched.current.orders) {
          touched.current.orders = false;
          void queryClient.invalidateQueries({ queryKey: qk.orders.all });
          void queryClient.invalidateQueries({ queryKey: qk.trips.all });
        }
      }, 2_000);
    },
    [queryClient],
  );
  useEffect(
    () => () => {
      if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
    },
    [],
  );

  const { status, lastMessageAt } = useEventStream('/dispatch/stream', {
    onMessage: handleMessage,
  });

  // Резервното допитване работи само докато потокът е паднал.
  useQuery({
    queryKey: [...qk.board.snapshot(), 'poll'],
    queryFn: async () => {
      const snapshot = await api.get<BoardSnapshot>('/dispatch/board');
      queryClient.setQueryData(qk.board.snapshot(), snapshot);
      return snapshot;
    },
    enabled: status === 'polling',
    refetchInterval: POLL_INTERVAL_MS,
    meta: { silent: true },
  });

  return {
    snapshot: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    streamStatus: status,
    lastMessageAt,
    refresh: () => void queryClient.invalidateQueries({ queryKey: qk.board.all }),
  };
}
